import {
  createEthersRpcProvider,
  normalizeChainIdAsNumber,
} from '@avantarte/smart-contracts/utilities/createEthersRpcProvider';
import {
  BaseContract,
  ContractReceipt,
  ContractTransaction,
  ethers,
} from 'ethers';

import { useWeb3 } from '../../../components/Web3/Web3Provider/useWeb3';
import { ContractConnection } from '../../../types/web3/contracts';
import { ErcChainId, TransactionError } from '../../../types/web3/web3';
import { blockTimestampToLocalDate } from '../../../utils/web3/time';

// wait for the transaction to be approved by the validators
const waitForTransactionApproval = (
  tx: ContractTransaction,
): Promise<ContractReceipt> => {
  return tx.wait();
};

// returns the reason for a failed transaction
export const getTransactionErrorReason = (err: TransactionError) => {
  const tError = (err.error ? err.error : err) as {
    code: number;
    message: string;
    data?: string;
  };
  // the solution that actually works based on trial and error
  if (tError?.message && tError.message.startsWith('execution reverted:')) {
    return tError.message.replace('execution reverted:', '').trim();
  }
  // the solution the internet suggests as a fallback
  if (tError.data && typeof tError.data === 'string') {
    const code = tError.data.replace('Reverted ', '');
    return ethers.utils.toUtf8String(`0x${code.substr(138)}`);
  }

  if (err.reason) {
    return err.reason;
  }
  // can't figure out the error
  return 'unknown';
};

export const useBlockchain = () => {
  const { chainId, web3Provider } = useWeb3();

  // fetches the current block information
  const getBlockInfo = async (blockId: number) => {
    const block = await web3Provider!.getBlock(blockId);
    return {
      date: blockTimestampToLocalDate(block.timestamp),
      block,
    };
  };

  // creates a contract connection to communicate with the contract on the blockchain
  const createContractConnection = <T extends BaseContract = ethers.Contract>(
    address: string,
    abi: any[],
    chain: ErcChainId,
  ): ContractConnection<T> => {
    if (web3Provider && chainId == chain) {
      return {
        address,
        chain,
        contract: new ethers.Contract(
          address,
          abi,
          web3Provider.getSigner(),
        ) as T,
        canSignTransactions: true,
      };
    }
    return {
      address,
      chain,
      contract: new ethers.Contract(
        address,
        abi,
        createEthersRpcProvider({
          chainId: normalizeChainIdAsNumber(chain),
          infura: {
            projectId: process.env.NEXT_PUBLIC_INFURA_PROJECT_ID!,
          },
        }),
      ) as T,
      canSignTransactions: false,
    };
  };

  // returns failure error code, or null if the transaction didn't fail
  const getTransactionFailureReason = async (
    tx: ContractTransaction,
  ): Promise<string | null> => {
    try {
      await web3Provider?.call(
        {
          data: tx.data,
          from: tx.from,
          chainId: tx.chainId,
          to: tx.to,
          value: tx.value,
          gasLimit: tx.gasLimit,
        },
        tx.blockNumber,
      );
      return null; // found no error
    } catch (err) {
      return getTransactionErrorReason(err);
    }
  };

  return {
    getTransactionErrorReason,
    waitForTransactionApproval,
    getBlockInfo,
    createContractConnection,
    getTransactionFailureReason,
  };
};
